import { useEffect, useRef, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { BreadcrumbBar } from './BreadcrumbBar';
import { Header } from './Header';
import { OnboardingRouteGuard } from './OnboardingRouteGuard';
import { ScrollContainerContext } from './scroll-container';
import { Sidebar } from './Sidebar';

/**
 * AppShell — fixed full-height shell: the header spans the full viewport
 * width; below it the sidebar owns the left edge and the content column
 * (breadcrumb + route content) sits to its right. Only the CONTENT column
 * scrolls — sidebar / header / breadcrumb stay pinned.
 *
 * SIDEBAR PROTOTYPE: the Subnav tab strip is replaced by the Sidebar; the
 * strip's slot now holds the BreadcrumbBar. Subnav.tsx is kept on disk
 * (unmounted) for easy A/B against main. The window no longer scrolls, so
 * the content container is shared via ScrollContainerContext — window-
 * scroll consumers (ScrollToTopButton, the wizard's section-change
 * scroll) read it and fall back to the window outside the shell.
 *
 * Auth-style pages (login, signup, admin) render WITHOUT the navbar — they
 * have their own AuthShell with logo-only chrome and shouldn't expose the
 * authenticated nav to anonymous visitors. We detect by path prefix so new
 * auth-adjacent routes (e.g. /forgot-password) drop in without touching
 * the shell wiring.
 *
 * Note: hero + main.wrap are PER-ROUTE (not in the shell) because the
 * vanilla site renders them per-page. Routes own their own hero copy +
 * wrap container — they center within the content column to the right of
 * the sidebar.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isAuthPath = AUTH_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));

  // The document no longer scrolls (the content div does), so reset the
  // container's scroll position on navigation — otherwise landing on a new
  // page keeps the previous page's scroll offset.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [path]);

  // Lock the DOCUMENT scroll while the authenticated shell is mounted so ONLY
  // the content column scrolls and the header + sidebar stay pinned. The shell
  // is h-screen, but tall/dynamic content (e.g. the dossier "Details used for
  // this dossier" preview that appears once a filing company + country are
  // chosen) can otherwise leak a little body scroll, dragging the pinned chrome
  // up at the bottom. Auth pages render without the shell and scroll normally,
  // so only lock when this shell is actually shown.
  useEffect(() => {
    if (isAuthPath) return;
    const html = document.documentElement;
    const { overflow: prevHtml } = html.style;
    const { overflow: prevBody } = document.body.style;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [isAuthPath]);

  if (isAuthPath) {
    return <>{children}</>;
  }

  return (
    <ScrollContainerContext.Provider value={contentRef}>
      <div className="flex flex-col h-screen">
        {/* Header spans the FULL width (the sidebar starts below the logo
            row); the breadcrumb lives INSIDE the content column so the
            navy rail runs unbroken from under the header to the bottom —
            a full-width breadcrumb would cut a white band across it. */}
        <Header />
        <div className="flex flex-1 min-h-0">
          <Sidebar />
          {/* min-h-0 on BOTH the column and the scroller is required: a flex
              child defaults to min-height:auto (its content size), which would
              let a tall page (e.g. the dossier form) overflow the h-screen
              shell and scroll the whole document — leaving the sidebar/header
              short at the bottom. min-h-0 caps them so overflow-y-auto scrolls
              the content column instead, keeping the chrome pinned.
              `relative` keeps absolutely-positioned descendants (Radix's hidden
              native <select>/radio inputs) inside this scroller. Without it they
              are placed against the page, sit far below the screen, and give the
              locked document room that scrollIntoView still scrolls: a blocked
              Submit dragged the header up and left the sidebar short. */}
          <div className="flex-1 min-w-0 min-h-0 flex flex-col">
            <BreadcrumbBar />
            <div ref={contentRef} className="relative flex-1 min-h-0 overflow-y-auto">
              {/* Route-level enforcement of the sidebar's first-run locks —
                  a typed URL to a locked section bounces to /dashboard. */}
              <OnboardingRouteGuard path={path}>{children}</OnboardingRouteGuard>
            </div>
          </div>
        </div>
      </div>
    </ScrollContainerContext.Provider>
  );
}

/**
 * Paths that render WITHOUT the authenticated navbar. Add new chrome-less
 * routes here (forgot-password, accept-invite, etc.).
 *
 * Admin paths are listed so the operator-only surface stays chrome-less —
 * the admin pages are real React routes but have their own AuthShell-style
 * indigo card chrome and don't expose the customer navbar.
 */
const AUTH_PATH_PREFIXES = ['/login', '/signup', '/admin', '/forgot-password', '/reset-password'];
